/*
  Neemans KwikCart skin + add-ons.
  Loaded by snippets/nms-kwikcart.liquid with data-direction="a" (Editorial) or "b" (Dense).

  What it does, in one 700 ms tick:
    1. Injects one <style> into GoKwik's open shadow root (idempotent).
    2. Deal of the Day block inside the drawer (product from CFG.deal), with an "Ends in" countdown.
       Hidden while the deal product is in the cart; returns when it is removed.
    3. Upsell title text that follows the item count.
    4. Green fill on tier icons once a tier is unlocked.
    5. Quick-view sheet: name/colour split, "Save ₹X", image counter, size pills that drive
       GoKwik's own select, and an "Add to cart · UK 8" label.
    6. Floating cart (storefront pages, outside the drawer), shown while the cart has items: a BAR
       (thumbnail, count, total, tier hint) where the page has no sticky bottom bar, a round MINI icon
       above the bar where it has one (product, collection). CFG.fab.bar = false keeps the round icon on every page,
       at the middle of the right edge (where the theme's floating cart button sat). Opens the drawer on tap, pushes
       cart_fab_view / cart_fab_click to dataLayer.
  It never rewrites GoKwik's own elements beyond those touches.
  Fail-safe: while the drawer is open a guard checks that GoKwik's checkout button and footer still exist
  and are on screen. If a GoKwik release changes its markup and that stops being true, the guard removes
  every nms style and block so GoKwik's default cart shows, and pushes nms_cart_guard_off to dataLayer.
  ?view=cart: added to the URL while the drawer is open and removed when it closes (replaceState, no reload);
  a link with ?view=cart opens the drawer on load. CFG.urlView = false turns this off.
  Clicks on non-cart elements that the GoKwik admin cart-icon selector also matches: snippets/nms-kwikcart-guard.liquid.
  Manual switch: add ?nms_cart=off to any URL (sticks for the tab session); ?nms_cart=on clears it.
  Colours that GoKwik exposes as admin fields (banner, upsell carousel, footer message)
  are set in the GoKwik admin, not here.
  Turn any feature off in CFG.features.
*/
(function () {
  'use strict';

  var script = document.currentScript || document.querySelector('script[data-direction]');
  var DIR = script && script.getAttribute('data-direction') === 'b' ? 'b' : 'a';

  var CFG = {
    features: { deal: true, upsellTitle: true, tierTicks: true, sheet: true, floatCart: true },
    deal: {
      handle: 'sole-max-cush-slip-ons-for-men-black',
      label: 'Deal of the Day',
      endHourIST: 24 // countdown to midnight IST ("today only")
    },
    tiers: [2, 3], // 4-item free-socks tier retired; socks sold via GoKwik one-tick upsell
    fab: {
      bar: false,     // false: round MINI icon on every page; true: BAR where the page has no sticky bottom bar
      slide: false,   // false: no slide-in on first show and no "Added · View cart" label sliding out after an add
      sticky: ['.nms-buy-buttons-container', '.collection-toolbar'], // known sticky bottom bars; any other fixed bottom bar is detected too
      barBottom: 0,   // BAR is docked to the screen edge; the search flyout and WhatsApp button are lifted above it (see FAB_CSS)
      miniBottom: 20, // MINI icon; raised by the height of a sticky bottom bar
      tier: { 1: 'Add 1 more · get 7% OFF', 2: 'Add 1 more · get 10% OFF', 3: '10% OFF unlocked' }
    },
    urlView: 'cart', // ?view=cart is in the URL while the drawer is open (no reload); a link with it opens the drawer. false = off
    sheetDefaultSize: 'UK 8', // preselected in the upsell quick-view sheet
    upsellTitle: {
      1: 'Add 1 more pair & save 7% OFF',
      2: 'Add a 3rd pair & save 10% OFF',
      3: 'Complete the look',
      4: 'Popular add-ons'
    }
  };

  /* ---------- CSS ---------- */

  var BASE = [
    '.sidecart,.sidecart *{--gokwik-primary-color:#1C1C1C!important}',
    '.progress-bar-fill{background:#175615!important}',
    '.progress-bar-rail{background:#DAD8D0!important}',
    '.milestone-goal,.milestone-text{color:#1C1C1C;font-weight:700}',
    '.icon-wrap{background:#fff;border-radius:50%;box-shadow:0 0 0 1.5px #175615;transition:background .2s}',
    '.icon-wrap svg path{stroke:#175615}',
    '.icon-wrap.nms-on{background:#175615!important}',
    '.icon-wrap.nms-on svg path{stroke:#fff!important}',
    '.powered-by,.price-chips,.promotion-banner .arrow{display:none!important}',
    '.header-text{font-family:"bebas-neue-pro",sans-serif;font-size:24px;letter-spacing:.02em}',
    '.side-cart-content-wrapper{padding-bottom:28px!important}',
    '.side-cart-content-wrapper .item-list-wrapper{order:0}',
    '.side-cart-content-wrapper .one-tick-upsell-container{order:0}',
    '.side-cart-content-wrapper .upSell-container{order:1}',
    '.side-cart-content-wrapper .nms-deal{order:2}',
    '.side-cart-content-wrapper .offer-box{order:3}',
    '.product-tile-wrapper{min-height:112px}',
    '.progress-container{min-height:88px}',
    '.upSell-container{min-height:150px}',
    '.offer-box{height:auto!important;min-height:0!important;flex:none!important;justify-content:flex-start!important}',
    '.sidecart-footer-wrapper .ribbon{position:static!important;display:block!important;width:100%!important;margin:0!important;border-radius:0!important;max-width:none!important;background:#175615!important;box-shadow:none!important}',
    '.sidecart-footer-wrapper .ribbon::before,.sidecart-footer-wrapper .ribbon::after{display:none!important}',
    '.sidecart-footer-wrapper .footer-ribbon-text{display:block!important;width:100%!important;text-align:center;padding:7px 12px!important;font-size:12px;font-weight:700;box-sizing:border-box;border-radius:0!important}',
    '.discount-banner,.gk-offers-row-count,.upSell-container .discount-banner{background:#175615!important;color:#fff!important}',
    '.upSell-container .scroll-container{gap:10px!important}',
    '.upSell-container .card{flex:0 0 84%!important;width:84%!important;max-width:none!important;min-width:0!important;scroll-snap-align:center}',
    '.upSell-container .product-name{width:100%!important;max-width:none!important;display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:1.3}',
    '.gk-code-box button,.offer-box button:not(.gk-offers-row):not(.gk-tab):not(.gk-coupon-toggle):not(:disabled){background:#1C1C1C!important;color:#fff!important;border-radius:8px}',
    '.gk-code-box button:disabled,.offer-box button:disabled{background:#E6E4DD!important;color:#6F6D66!important}',
    '.offer-box div.apply-btn.remove-btn button:not(.a):not(.b):not(.c):not(.d):not(:disabled){background:#fff!important;border:1px solid #1C1C1C!important;border-radius:50%!important;width:26px;height:26px;padding:0}',
    '.offer-box .remove-btn button img{width:12px;height:12px}',
    '.offer-box .apply-btn button:not(.remove-btn button){padding:4px 12px!important;min-width:56px}',
    '.add-btn{background:#fff;color:#1C1C1C;border:1.5px solid #1C1C1C;border-radius:8px}',
    '.gokwik-checkout button:disabled{background:#B9B7AF!important}',
    /* quick view sheet (GoKwik product popup): compact card. Opened by the upsell "Add" and the
       line-item size pill. JS (syncSheet) splits the title, adds "Save ₹X", the image counter,
       the size pills and the "Add to cart · UK 8" label; everything else is CSS. */
    '.container.top-close-button .sheet-wrapper{background:#F3F2EE!important;border-radius:16px 16px 0 0!important;overflow:hidden;display:flex!important;flex-direction:column;max-height:88vh!important;max-height:88dvh!important}',
    '.sheet-wrapper .head{position:relative;height:40px!important;padding:0!important;border:0!important;background:transparent!important;flex:none}',
    '.sheet-wrapper .head::before{content:"";position:absolute;left:50%;top:10px;width:40px;height:4px;margin-left:-20px;border-radius:2px;background:#CECDCA}',
    '.sheet-wrapper .bottom-sheet-title{display:none!important}',
    '.sheet-wrapper .close-button{position:absolute!important;top:4px!important;right:12px!important;left:auto!important;bottom:auto!important;width:32px!important;height:32px!important;margin:0!important;border-radius:50%!important;background:#fff!important;color:#1C1C1C!important;border:0!important;box-shadow:none!important;font-size:18px!important;display:flex;align-items:center;justify-content:center}',
    /* GoKwik paints a scroll-shadow gradient on .content (white fade + radial shadows); it greys out the surround and fades the card edges, so it is removed. */
    '.sheet-wrapper .content{flex:1;min-height:0;overflow-y:auto;padding:4px 0 0!important;background:none!important}',
    '.sheet-wrapper .product-popup{padding:0!important}',
    '.sheet-wrapper .product-popup-content{display:grid!important;grid-template-columns:120px minmax(0,1fr);grid-template-rows:auto auto 1fr auto auto auto;column-gap:12px;row-gap:0;align-content:start;width:auto!important;margin:0 12px 12px!important;padding:12px!important;background:#fff!important;border-radius:16px;min-height:0!important}',
    '.sheet-wrapper .product-details{display:contents!important}',
    '.sheet-wrapper .product-image{grid-column:1;grid-row:1/4;width:120px!important;height:120px!important;margin:0!important;padding:0!important;background:#F3F2EE;border-radius:12px;overflow:hidden;position:relative}',
    '.sheet-wrapper .image-carousel,.sheet-wrapper .main-image-container{width:120px!important;height:120px!important;position:relative!important;overflow:hidden!important}',
    '.sheet-wrapper .main-image{width:120px!important;max-width:none!important;height:120px!important;object-fit:cover!important;display:block}',
    '.sheet-wrapper .carousel-dots{display:none!important}',
    '.sheet-wrapper .arrow-btn{position:absolute!important;top:50%;margin-top:-11px;width:22px!important;height:22px!important;border-radius:50%;background:rgba(255,255,255,.85)!important;color:#1C1C1C!important;box-shadow:none!important;outline:0;z-index:2}',
    '.sheet-wrapper .arrow-btn svg{width:12px!important;height:12px!important}',
    '.sheet-wrapper .arrow-left{left:4px!important}',
    '.sheet-wrapper .arrow-right{right:4px!important}',
    '.sheet-wrapper .main-image-container[data-nms-count]::after{content:attr(data-nms-count);position:absolute;right:6px;bottom:6px;padding:2px 6px;border-radius:6px;background:rgba(28,28,28,.72);color:#fff;font-size:10px;font-weight:600;line-height:14px;z-index:2}',
    '.sheet-wrapper .product-title{grid-column:2;grid-row:1;display:flex;flex-direction:column;gap:2px;font-size:15px!important;font-weight:700;line-height:1.25;color:#1C1C1C;margin:0!important}',
    '.sheet-wrapper .product-title .nms-pt-colour{font-size:13px;font-weight:500;color:#737373}',
    '.sheet-wrapper .product-price{grid-column:2;grid-row:2;display:flex!important;flex-wrap:wrap;align-items:baseline;gap:2px 8px;margin:8px 0 0!important;order:0}',
    '.sheet-wrapper .product-price .price{order:-1;font-size:18px;font-weight:800;line-height:1.2;color:#1C1C1C}',
    '.sheet-wrapper .product-price strike{font-size:13px;color:#9A9A9A}',
    '.sheet-wrapper .product-price .nms-save{flex-basis:100%;font-size:13px;font-weight:700;color:#068743}',
    '.sheet-wrapper .view-details-section{grid-column:2;grid-row:3;align-self:start;order:0;padding:0!important;margin:8px 0 0!important;border:0!important;text-align:left}',
    '.sheet-wrapper .view-details-btn{display:inline-flex!important;align-items:center;gap:4px;width:auto!important;height:auto!important;padding:0!important;background:transparent!important;color:#1C1C1C!important;border:0!important;border-radius:0!important;font-size:13px!important;font-weight:700;text-decoration:underline!important;text-underline-offset:3px}',
    '.sheet-wrapper .view-details-btn span{font-size:0}',
    '.sheet-wrapper .view-details-btn span::before{content:"View full details";font-size:13px}',
    '.sheet-wrapper .view-details-btn svg{width:12px!important;height:12px!important}',
    '.sheet-wrapper .product-description{grid-column:1/3;grid-row:4;margin:12px 0 0!important;max-height:none!important;overflow:visible!important;font-size:13px!important;line-height:1.45;color:#3D3C38!important}',
    '.sheet-wrapper .product-description p:first-child{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin:0}',
    '.sheet-wrapper .product-description p:not(:first-child),.sheet-wrapper .product-description ul{display:none}',
    '.sheet-wrapper .variant-info{grid-column:1/3;grid-row:5;margin:14px 0 0!important;padding:0!important;display:block!important}',
    '.sheet-wrapper .variant-label,.sheet-wrapper .product-options{display:none!important}',
    '.sheet-wrapper .variant-list{margin:0!important}',
    '.sheet-wrapper .variant-list,.sheet-wrapper .variant-selector{display:block!important;width:100%!important;max-width:none!important;position:relative}',
    /* the native select stays in the DOM (GoKwik binds to it); the pills drive it */
    '.sheet-wrapper .variant-selector select{position:absolute!important;width:1px!important;height:1px!important;opacity:0;pointer-events:none;margin:0;padding:0;border:0}',
    '.sheet-wrapper .nms-sizes{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;margin:0 -12px;padding:2px 12px}',
    '.sheet-wrapper .nms-sizes::-webkit-scrollbar{display:none}',
    '.sheet-wrapper .nms-size{flex:none;width:48px;height:48px;border-radius:50%;border:1px solid #CECDCA;background:#fff;color:#1C1C1C;font-family:inherit;font-size:13px;font-weight:600;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;margin:0}',
    '.sheet-wrapper .nms-size.is-on{background:#1C1C1C;color:#fff;border-color:#1C1C1C}',
    '.sheet-wrapper .nms-size[disabled]{opacity:.35;text-decoration:line-through;cursor:default}',
    '.sheet-wrapper .add-to-cart-section{grid-column:1/3;grid-row:6;position:sticky;bottom:0;order:0;background:#fff;padding:14px 0 0!important;margin:0!important;z-index:3}',
    '.sheet-wrapper .add-to-cart-btn{position:static!important;width:100%;height:52px;border-radius:12px;background:#1C1C1C!important;color:#fff!important;border:0!important;font-size:15px;font-weight:700;justify-content:center;align-items:center;box-shadow:none!important;margin:0!important}',
    /* deal block (shared). Markup is ours (dealHtml), so these are plain rules. */
    '.nms-deal{box-sizing:border-box;width:100%;height:auto!important;flex:none!important}',
    '.nms-deal *{box-sizing:border-box}',
    '.nms-deal__head{display:flex;justify-content:space-between;align-items:center;gap:8px;font-weight:700}',
    '.nms-deal__timer{display:inline-flex;align-items:center;gap:5px;font-variant-numeric:tabular-nums;font-weight:600;white-space:nowrap}',
    '.nms-deal__timer b{display:inline-block;min-width:24px;text-align:center;border-radius:4px;padding:2px 4px;font-weight:500;line-height:20px}',
    '.nms-deal__body{display:flex;flex-direction:column;gap:8px}',
    '.nms-deal__row{display:flex;gap:12px;align-items:center}',
    '.nms-deal__img{width:56px;height:56px;border-radius:8px;object-fit:cover;background:#F3F2EE;flex:none}',
    '.nms-deal__info{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px}',
    '.nms-deal__name{font-size:12px;font-weight:500;line-height:16px;color:#1C1C1C;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
    '.nms-deal__price{display:flex;align-items:center;gap:6px;line-height:16px;flex-wrap:wrap}',
    '.nms-deal__price b{font-size:12px;font-weight:700;color:#3A3A3A}',
    '.nms-deal__price s{font-size:12px;font-weight:400;color:#848482}',
    '.nms-deal__price em{font-style:normal;font-size:12px;font-weight:700;color:#468A44}',
    '.nms-deal__size{flex:none;width:84px;height:36px;border:0;border-radius:8px;padding:0 28px 0 12px;font-size:12px;font-weight:500;font-family:inherit;color:#1C1C1C;background:#F3F4F6 url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 14 14%27 fill=%27none%27 stroke=%27%231C1C1C%27 stroke-width=%271.5%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M3.5 5.5 7 9l3.5-3.5%27/%3E%3C/svg%3E") no-repeat right 10px center/14px 14px;-webkit-appearance:none;appearance:none}',
    '.nms-deal__add{flex:1;min-width:0;height:36px;display:flex;align-items:center;justify-content:center;gap:8px;border:0;border-radius:999px;background:#3A3A3A;color:#fff;font-size:12px;font-weight:500;font-family:inherit;cursor:pointer;padding:0 16px}',
    '.nms-deal__add::before{content:"";width:16px;height:16px;flex:none;background:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%23fff%27 stroke-width=%272.2%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M2 3h3l2.4 11.2a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.5L22 7H6.2%27/%3E%3Ccircle cx=%2710%27 cy=%2720%27 r=%271.3%27/%3E%3Ccircle cx=%2718%27 cy=%2720%27 r=%271.3%27/%3E%3Cpath d=%27M14 7v6M11 10h6%27/%3E%3C/svg%3E") center/contain no-repeat}',
    '.nms-deal__add[disabled]{background:#175615;cursor:default}',
    '.nms-deal__add[disabled]::before{display:none}'
  ];

  var SKIN = {
    /* A: Editorial. White drawer, hairline sections, Bebas titles, pill buttons. */
    a: [
      '.sidecart{background:#fff!important}',
      '.side-cart-header{padding:14px 20px 6px!important;border:0!important;background:#fff!important}',
      '.header-text{font-size:30px;letter-spacing:.03em}',
      '.promotion-banner{background:#fff!important;padding:0 20px 10px!important}',
      '.promotion-banner,.promotion-banner *{color:#6F6D66!important;font-size:12px!important;font-weight:500!important;text-align:left!important}',
      '.progress-container{background:#F7F6F2!important;border:0!important;border-radius:0!important;margin:0!important;padding:14px 20px 16px!important}',
      '.side-cart-content-wrapper{padding:0!important;gap:0!important;background:#fff!important}',
      '.side-cart-content-wrapper>*{background:#fff!important;border:0!important;border-top:1px solid #ECEAE3!important;border-radius:0!important;box-shadow:none!important;margin:0!important;padding:16px 20px!important}',
      '.product-tile-wrapper{border:0!important;box-shadow:none!important;border-radius:0!important;padding:0!important;margin:0 0 14px!important}',
      '.product-tile-wrapper:last-child{margin-bottom:0!important}',
      '.upSell-container .title{font-family:"bebas-neue-pro",sans-serif;font-size:20px;letter-spacing:.03em;font-weight:400!important;text-transform:uppercase;margin-bottom:10px}',
      '.upSell-container .card{background:#fff!important;border:0!important;box-shadow:none!important;border-radius:0!important;padding:0!important;flex:0 0 150px!important;width:150px!important;flex-direction:column!important}',
      '.add-btn,.nms-deal__add,.offer-box .apply-btn button{border-radius:999px!important}',
      '.gokwik-checkout button{border-radius:999px!important}',
      '.sidecart-footer-wrapper{border-top:1px solid #ECEAE3;background:#fff}',
      '.sidecart-footer-wrapper .ribbon{background:#fff!important}',
      '.sidecart-footer-wrapper .footer-ribbon-text{color:#175615!important;text-align:left;padding:8px 20px 0!important;background:#fff!important}',
      '.nms-deal__head{font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#B3261E;margin-bottom:8px}',
      '.nms-deal__timer{color:#1C1C1C;font-size:12px}',
      '.nms-deal__timer b{background:#1C1C1C;color:#fff}'
    ],
    /* B: Dense. Grey canvas, white cards, dark USP strip, chips and badges. Compact spacing. */
    b: [
      '.sidecart{background:#F3F2EE!important;justify-content:flex-start!important}',
      '.side-cart-header,.promotion-banner{border-radius:0!important}',
      '.side-cart-header{background:#fff!important;border-bottom:1px solid #CECDCA!important;padding:4px 8px 4px 12px!important;display:flex!important;align-items:center!important;gap:8px}',
      '.side-cart-subHeader{flex:1;min-width:0;height:auto!important;padding:0!important;display:flex!important;align-items:center}',
      '.side-cart-subHeader p{margin:0!important;padding:0!important;line-height:28px;display:flex;align-items:center}',
      '.header-text{display:block;font-size:24px;line-height:28px;letter-spacing:.015em;color:#3A3A3A}',
      '.close-btn{width:40px!important;height:40px!important;margin:0!important;display:flex!important;align-items:center;justify-content:center;border-radius:999px;flex:none}',
      '.close-btn img{width:20px;height:20px;display:block}',
      '.promotion-banner{padding:4px 12px!important}',
      '.promotion-banner *{font-size:11px!important;font-weight:600!important}',
      /* tier card (Figma 2147230413). GoKwik's milestone row is fixed at 50px with absolute children, so the row heights are set to fit 24px icons. */
      '.progress-container{background:#D4DDD1!important;border:0!important;border-radius:10px!important;margin:8px 12px 0!important;padding:8px 12px 10px!important;min-height:0!important;height:auto!important;flex:none!important}',
      '.next-milestone-text{font-size:14px!important;font-weight:600!important;line-height:18px;color:#1C1C1C!important;text-align:center;margin:0 0 6px!important}',
      '.progress-container .container,.milestone-wrap,.milestones{height:48px!important}',
      '.progress-bar-rail{background:#fff!important;height:6px!important;top:11px!important;border-radius:99px;overflow:visible!important}',
      '.progress-bar-fill{background:#5EA45C!important;height:8px!important;margin-top:-1px;border-radius:99px}',
      '.milestone-content{top:-23px!important;gap:4px!important}',
      '.milestone-goal{font-size:12px!important;font-weight:500!important;color:#3A3A3A!important;line-height:16px;display:none!important}',
      '.milestone-text{font-size:12px!important;font-weight:700!important;color:#175615!important;line-height:16px}',
      '.icon-wrap{width:28px!important;height:28px!important;box-sizing:border-box;border:1px solid #175615;box-shadow:inset 0 0 0 1.5px #fff,inset 0 0 0 3px #175615}',
      '.icon-wrap svg{width:18px!important;height:16px!important}',
      '.icon-wrap.nms-on{box-shadow:none!important}',
      '.side-cart-content-wrapper{padding:12px 12px 16px!important;gap:16px!important;display:flex;flex-direction:column}',
      '.side-cart-content-wrapper>*{background:#fff!important;border:0!important;border-radius:10px!important;box-shadow:none!important;margin:0!important;padding:10px 12px!important}',
      /* line item (Figma: one white card per item, image | title / price row / size + qty + trash).
         GoKwik renders price+qty as a right-hand column; the grid + display:contents reflows it without touching the DOM. */
      '.item-list-wrapper{background:#fff!important;padding:0 12px!important;border-radius:12px!important;flex:none!important;height:auto!important}',
      '.product-tile-wrapper{background:transparent!important;border:0!important;box-shadow:none!important;border-radius:0!important;padding:14px 0!important;margin:0!important;min-height:92px!important}',
      '.product-tile-wrapper+.product-tile-wrapper{border-top:1px dashed #CECDCA!important}',
      '.product-tile-wrapper .item{display:grid!important;grid-template-columns:76px minmax(0,1fr);grid-template-rows:auto auto auto;column-gap:8px;row-gap:8px;padding:0!important;align-items:center;min-height:0!important;height:auto!important}',
      '.product-tile-wrapper .product-main-content,.product-tile-wrapper .details{display:contents!important}',
      '.product-tile-wrapper .img{grid-row:1/4;grid-column:1;width:76px!important;height:76px!important;align-self:center;background:#F8F7F4;border-radius:6px;overflow:hidden}',
      '.product-tile-wrapper .img img{width:100%!important;height:100%!important;object-fit:cover}',
      '.sidecart-product-title{grid-row:1;grid-column:2;font-size:12px!important;font-weight:500!important;line-height:16px!important;color:#1C1C1C!important;margin:0!important;display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
      '.product-tile-wrapper .variant-list{grid-row:3;grid-column:2;margin:0!important;align-self:center;justify-self:start;position:relative;z-index:1}',
      '.product-tile-wrapper .variant-selector-readonly{margin:0!important;height:26px;box-sizing:border-box;font-size:12px;font-weight:500;color:#1C1C1C}',
      '.product-tile-wrapper .item-price{grid-row:2/4;grid-column:2;width:auto!important;min-width:0;text-align:left!important;display:grid!important;grid-template-columns:auto auto auto minmax(0,1fr) auto;column-gap:6px;row-gap:8px;align-items:center;line-height:16px}',
      '.product-tile-wrapper .final-price{grid-area:1/1;font-size:12px!important;font-weight:700!important;color:#3A3A3A!important}',
      '.product-tile-wrapper .compare-at-price{grid-area:1/2;font-size:12px!important;font-weight:400;color:#848482!important}',
      /* "(63% OFF)" hidden: MRP strike + the extra-discount chip already say it; three discount signals per line was the main clutter. */
      '.product-tile-wrapper .discount-percentage{display:none!important}',
      /* chip: plain green text inline after the MRP (no pill, no icon) so it fits the ~268px row. */
      '.product-tile-wrapper .coupon-applied-area{grid-area:1/3/2/6;justify-self:start;margin:0!important;background:transparent!important;padding:0!important;gap:0!important}',
      '.product-tile-wrapper .coupon-applied-area .icon{display:none!important}',
      '.product-tile-wrapper .coupon-applied-area span{font-size:12px!important;font-weight:700!important;color:#468A44!important}',
      '.product-tile-wrapper .quantity-handler-wrapper{grid-area:2/1/3/6;display:flex!important;justify-content:flex-end;align-items:center;gap:16px;margin:0!important}',
      '.product-tile-wrapper .quantity-handler{display:flex!important;width:auto!important;height:25px;padding:0 8px!important;gap:3px;box-sizing:border-box;align-items:center;border-radius:10px!important}',
      '.product-tile-wrapper .reduce-quantity,.product-tile-wrapper .increase-quantity{width:auto!important;min-width:0!important;height:23px;padding:0!important;font-size:13px!important;font-weight:600;line-height:1;color:#1C1C1C;display:flex;align-items:center}',
      '.product-tile-wrapper .reduce-quantity{padding-right:5px!important}',
      '.product-tile-wrapper .increase-quantity{padding-left:5px!important}',
      '.product-tile-wrapper .item-quantity{width:30px!important;padding:0 5px!important;margin:0!important;text-align:center;font-size:13px!important;font-weight:600;height:17px!important;line-height:17px;color:#000;border:0!important;border-left:1px solid #E5E7EB!important;border-right:1px solid #E5E7EB!important;border-radius:0!important;background:transparent!important}',
      '.product-tile-wrapper .remove-btn{height:16px;display:flex;align-items:center}',
      /* one-tick socks upsell (GoKwik Cart Capabilities > One Tick Upsell; copy and product are admin fields).
         Rendered as checkbox | thumb | label; the thumb is the Grey pack image, swap the URL if the product changes. */
      '.one-tick-upsell-container{background:#fff!important;border-radius:10px!important;padding:0!important;margin:0!important;flex:none!important;height:auto!important}',
      '.one-tick-upsell-checkbox{display:flex!important;align-items:center;gap:12px;width:auto!important;padding:10px 12px!important;min-height:64px;box-sizing:border-box;cursor:pointer}',
      '.one-tick-upsell-checkbox::after{content:"";order:1;width:44px;height:44px;flex:none;border-radius:8px;background:#F3F2EE url("https://cdn.shopify.com/s/files/1/2428/5565/files/ND-ANHT-Grey_WebCatalog_a_88x88.jpg?v=1767360118") center/cover no-repeat}',
      '.one-tick-upsell-checkbox input[type=checkbox]{order:0;width:20px!important;height:20px!important;margin:0!important;flex:none;accent-color:#175615;cursor:pointer}',
      '.one-tick-upsell-checkbox .label-text{order:2;flex:1;min-width:0;margin:0!important;padding:0!important;cursor:pointer}',
      '.one-tick-upsell-checkbox .label-text p{margin:0!important;font-size:13px!important;font-weight:500!important;line-height:18px;color:#1C1C1C!important}',
      /* upsell (Figma: sage section, white card strip, 97px image tile, pill "Add to Cart") */
      '.upSell-container{background:#fff!important;border:1px solid #E6E4DD!important;padding:12px 0 12px 12px!important;border-radius:10px!important;flex:none!important;height:auto!important}',
      '.upSell-container .title{font-size:14px!important;font-weight:700!important;line-height:20px;color:#1C1C1C!important;margin:0 0 10px!important;display:block;background:transparent!important;padding:0!important}',
      '.upSell-container .scroll-container{padding:0!important;gap:0!important}',
      '.upSell-container .card{background:#fff!important;border:1px solid #E6E4DD!important;border-radius:8px!important;margin-right:8px!important;flex:0 0 245px!important;width:245px!important;padding:8px!important;align-items:flex-start!important;scroll-snap-align:start}',
      '.upSell-container .product-title-button{width:97px!important;height:97px!important;flex:none}',
      '.upSell-container .card-left-container{width:97px!important;height:97px!important;background:#F8F7F4;border-radius:6px;overflow:hidden;display:flex;flex-direction:column}',
      '.upSell-container .product-image{width:97px!important;height:80px!important;object-fit:contain!important;border-radius:0}',
      '.upSell-container .card-left-container .discount-banner{background:#468A44!important;border-radius:0 0 8px 8px;height:17px;padding:2px 0!important;margin:0!important;font-size:11px!important;font-weight:700!important;line-height:13px}',
      '.upSell-container .card-right-container{padding:0 0 0 12px!important;margin:0!important;height:97px!important;min-width:0;display:flex!important;flex-direction:column;justify-content:center;gap:8px}',
      '.upSell-container .product-name{font-size:14px!important;font-weight:400!important;line-height:18px!important;color:#00192F!important}',
      '.upSell-container .card .price{margin:0!important;display:flex;align-items:center;gap:6px;line-height:16px}',
      '.upSell-container .card .price br{display:none}',
      '.upSell-container .card .price strike{font-size:12px!important;font-weight:400;color:#848482!important}',
      '.upSell-container .card .price span{font-size:12px!important;font-weight:700!important;color:#3A3A3A!important}',
      /* Label swap is visual only (GoKwik emits "+ Add"); the accessible name is unchanged. Set the label in GoKwik admin at rollout if the field exists. */
      '.upSell-container .add-btn{height:28px!important;width:120px;max-width:100%;padding:0 8px!important;border:1px solid #3A3A3A!important;border-radius:999px!important;background:#fff!important;color:#3A3A3A!important;font-size:0!important;display:flex!important;align-items:center;justify-content:center;gap:8px}',
      '.upSell-container .add-btn::before{content:"";width:12px;height:12px;flex:none;background:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%233A3A3A%27 stroke-width=%272.2%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M2 3h3l2.4 11.2a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.5L22 7H6.2%27/%3E%3Ccircle cx=%2710%27 cy=%2720%27 r=%271.3%27/%3E%3Ccircle cx=%2718%27 cy=%2720%27 r=%271.3%27/%3E%3Cpath d=%27M14 7v6M11 10h6%27/%3E%3C/svg%3E") center/contain no-repeat}',
      '.upSell-container .add-btn::after{content:"Add to Cart";font-size:12px;font-weight:500;line-height:16px}',
      /* offers (Figma: coupon rows on #F3F2EE, "You save" chip, accent "Apply" link or bare X, divider, View-all row with icon tile and count pill) */
      '.offer-box{padding:0!important;border-radius:10px!important;overflow:hidden}',
      '.offer-box .featured-offer-wrapper{margin:0!important;padding:12px 12px 0!important}',
      '.offer-box{padding-bottom:0!important}',
      /* Not-yet-applied state: GoKwik drops the wrapper and puts .featured-offer straight in .offer-box. */
      '.offer-box>.featured-offer{margin:12px 12px 0!important}',
      '.offer-box .gk-under-featured{margin:0!important}',
      '.offer-box .featured-offer{background:#F3F2EE!important;border:0!important;border-radius:8px!important;padding:8px!important;gap:16px;align-items:center;min-height:44px}',
      '.offer-box .offer-details{gap:8px;align-items:center;flex:1;min-width:0;flex-wrap:wrap}',
      '.offer-box .offer-icon,.offer-box .offer-icon svg{width:20px!important;height:20px!important}',
      '.offer-box .offer-title{font-size:14px!important;font-weight:500!important;line-height:20px;color:#1C1C1C;gap:4px}',
      '.offer-box .offer-title p{font-size:14px!important;font-weight:500!important;text-transform:uppercase}',
      '.offer-box .applied-text{display:none!important}',
      '.offer-box .amount-saved{background:rgba(6,135,67,.1)!important;color:#068743!important;border-radius:5px!important;padding:4px 8px!important;font-size:12px!important;font-weight:500!important;line-height:16px;height:auto;display:inline-flex;align-items:center;gap:3px;white-space:nowrap}',
      '.offer-box .featured-offer .apply-btn button:not(.a):not(.b):not(.c):not(.d):not(.e):not(:disabled){background:transparent!important;color:#B78742!important;border:0!important;border-radius:0!important;padding:4px!important;min-width:0!important;font-size:14px!important;font-weight:500!important;box-shadow:none!important}',
      '.offer-box div.apply-btn.remove-btn button:not(.a):not(.b):not(.c):not(.d):not(.e):not(.f):not(:disabled){background:transparent!important;border:0!important;width:24px!important;height:24px!important;padding:4px!important;border-radius:0!important}',
      '.offer-box .remove-btn button img{width:16px!important;height:16px!important;opacity:0}',
      '.offer-box div.apply-btn.remove-btn button:not(.a):not(.b):not(.c):not(.d):not(.e):not(.f):not(.g):not(:disabled){background:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27none%27 stroke=%27%23B78742%27 stroke-width=%271.6%27 stroke-linecap=%27round%27%3E%3Cpath d=%27M4 4l8 8M12 4l-8 8%27/%3E%3C/svg%3E") center/16px 16px no-repeat!important}',
      '.offer-box .gk-offers-row>svg{color:#B78742!important;width:16px;height:16px}',
      /* Third variant after a coupon is removed: .gk-best-coupon (icon tile, code + save line, dark Apply). Restyled to the same row. */
      '.offer-box .gk-best-coupon{margin:12px 12px 0!important;padding:8px!important;width:auto!important;box-sizing:border-box;background:#F3F2EE;border-radius:8px;display:flex;align-items:center;gap:16px}',
      '.offer-box .gk-best-icon{width:20px!important;height:20px!important;background:transparent!important;margin:0!important;flex:none;display:flex;align-items:center;justify-content:center}',
      '.offer-box .gk-best-text{flex:1;min-width:0;display:flex!important;flex-direction:row!important;justify-content:flex-start;align-items:center;gap:8px;flex-wrap:wrap}',
      '.offer-box .gk-best-code{font-size:14px!important;font-weight:500!important;line-height:20px;color:#1C1C1C!important;text-transform:uppercase;margin:0!important}',
      '.offer-box .gk-best-save{background:rgba(6,135,67,.1);color:#068743!important;border-radius:5px;padding:4px 8px;font-size:12px!important;font-weight:500!important;line-height:16px;white-space:nowrap;overflow:visible!important}',
      '.offer-box .gk-best-apply:not(.a):not(.b):not(.c):not(.d):not(.e):not(:disabled){background:transparent!important;color:#B78742!important;border:0!important;border-radius:0!important;padding:4px!important;min-width:0!important;height:auto!important;font-size:14px!important;font-weight:500!important;box-shadow:none!important}',
      '.offer-box .gk-coupon-entry{margin:0!important;padding:10px 12px 12px!important}',
      '.offer-box .gk-coupon-toggle{font-size:14px;font-weight:500;color:#B78742!important;text-decoration:none;padding:4px 0!important;background:transparent!important;border:0!important;border-radius:0!important}',
      '.offer-box .gk-coupon-input{margin-top:8px}',
      '.offer-box .gk-code-box input{background:#F3F2EE!important;border:0!important;border-radius:8px!important;height:40px;font-size:14px}',
      '.offer-box .gk-code-box button{border-radius:999px!important;height:40px;padding:0 16px!important}',
      '.offer-box .gk-offers-divider{margin:0!important;background:#CECDCA!important;height:1px}',
      '.offer-box .gk-offers-row{padding:12px!important;min-height:0!important;gap:8px;align-items:center}',
      '.offer-box .gk-offers-row-icon{width:34px!important;height:34px!important;background:#F3F2EE!important;border-radius:8px!important;margin:0!important;flex:none}',
      '.offer-box .gk-offers-row-icon svg{width:16px;height:16px}',
      '.offer-box .gk-offers-row-title{font-size:14px!important;font-weight:500!important;line-height:20px;color:#292929!important}',
      '.offer-box .gk-offers-row-subtitle{font-size:12px!important;font-weight:500!important;line-height:16px;color:#737373!important}',
      '.offer-box .gk-offers-row-count{background:#E9E1D6!important;color:#3A3A3A!important;font-size:10px!important;font-weight:700!important;line-height:14px;height:20px;min-width:20px;padding:0 7px!important;border-radius:999px!important;display:inline-flex;align-items:center;justify-content:center}',
      /* footer: white sheet, savings pill, total row, 55px button (Figma 2147230414) */
      '.sidecart-footer-wrapper{background:#fff;border-top:0;border-radius:16px 16px 0 0;padding:8px 12px 10px!important;box-shadow:0 -2px 10px rgba(0,0,0,.05)}',
      '.sidecart-footer-wrapper .ribbon{background:transparent!important;border-radius:0!important;margin:0!important}',
      '.sidecart-footer-wrapper .footer-ribbon-text{display:flex!important;align-items:center;gap:6px;padding:0 0 4px!important;font-size:12px;font-weight:600;line-height:16px;color:#175615!important;text-align:left}',
      '.sidecart-footer-wrapper .footer-ribbon-text::before{content:"";flex:none;width:16px;height:16px;background:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3E%3Cpath fill=%27%23175615%27 d=%27M8 .6l1.7 1.3 2.1-.4.8 2 2 .8-.4 2.1L15.4 8l-1.2 1.6.4 2.1-2 .8-.8 2-2.1-.4L8 15.4l-1.7-1.3-2.1.4-.8-2-2-.8.4-2.1L.6 8l1.2-1.6-.4-2.1 2-.8.8-2 2.1.4z%27/%3E%3Ccircle cx=%275.9%27 cy=%275.9%27 r=%271.3%27 fill=%27%23fff%27/%3E%3Ccircle cx=%2710.1%27 cy=%2710.1%27 r=%271.3%27 fill=%27%23fff%27/%3E%3Cpath d=%27M5 11l6-6%27 stroke=%27%23fff%27 stroke-width=%271.4%27 stroke-linecap=%27round%27/%3E%3C/svg%3E") center/contain no-repeat}',
      '.sidecart-footer{padding:0!important}',
      '.gokwik-button-wrapper{width:100%!important}',
      '.total-amount{padding:2px 0!important;align-items:center}',
      '.estimated-total-container strike,.estimated-total-container .discount-percentage{display:none!important}',
      '.gokwik-checkout{height:48px!important;margin-top:4px}',
      '.gokwik-checkout button{border-radius:10px!important}',
      /* deal (Figma: red-outlined card, rose head with "Ends in" boxes, 74px image, size pill + full-width dark pill button) */
      '.nms-deal{padding:0!important;overflow:hidden;border:1px solid #B44943!important;border-radius:10px!important}',
      '.nms-deal__head{background:#F2D0CE;padding:8px 12px;font-size:14px;line-height:20px;color:#3A3A3A}',
      '.nms-deal__timer{color:#B44943;font-size:12px;line-height:16px}',
      '.nms-deal__timer b{background:#B44943;color:#F2D0CE;font-size:14px}',
      '.nms-deal__body{padding:10px 12px}'
    ]
  };

  var CSS = BASE.concat(SKIN[DIR]).join('\n');

  /* ---------- helpers ---------- */

  function root() {
    var host = document.querySelector('shadow-dom-container');
    return host && host.shadowRoot;
  }

  function itemCount(sr) {
    var h = sr.querySelector('.header-text');
    var m = h && /\((\d+)/.exec(h.textContent || '');
    return m ? parseInt(m[1], 10) : 0;
  }

  function inr(paise) {
    return '₹' + Math.round(paise / 100).toLocaleString('en-IN');
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function timeLeft() {
    var IST = 19800000;
    var now = new Date(Date.now() + IST);
    var end = new Date(now);
    end.setUTCHours(CFG.deal.endHourIST, 0, 0, 0);
    var s = Math.max(0, Math.floor((end - now) / 1000));
    return { h: pad(Math.floor(s / 3600)), m: pad(Math.floor(s % 3600 / 60)), s: pad(s % 60) };
  }

  function timerHtml(t) {
    return 'Ends in <b data-u="h">' + t.h + '</b> h <b data-u="m">' + t.m + '</b> m <b data-u="s">' + t.s + '</b> s';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---------- deal of the day ---------- */

  var deal = { product: null, loading: false, added: false };

  function loadDeal() {
    if (deal.product || deal.loading) return;
    deal.loading = true;
    fetch('/products/' + CFG.deal.handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { deal.product = p; deal.loading = false; })
      .catch(function () { deal.loading = false; });
  }

  function dealHtml(p) {
    var avail = p.variants.filter(function (v) { return v.available; });
    if (!avail.length) return '';
    var off = p.compare_at_price > p.price ? Math.round((p.compare_at_price - p.price) / p.compare_at_price * 100) + '% OFF' : '';
    var preferred = ['UK 8', 'UK 9', 'UK 7'];
    var def = avail.filter(function (v) { return preferred.indexOf(v.title) > -1; })
      .sort(function (a, b) { return preferred.indexOf(a.title) - preferred.indexOf(b.title); })[0] || avail[0];
    var opts = avail.map(function (v) {
      return '<option value="' + v.id + '"' + (v.id === def.id ? ' selected' : '') + '>' + escapeHtml(v.title) + '</option>';
    }).join('');
    var img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x200$1$2') : '';
    return (
      '<div class="nms-deal__head"><span>' + escapeHtml(CFG.deal.label) + '</span><span class="nms-deal__timer">' + timerHtml(timeLeft()) + '</span></div>' +
      '<div class="nms-deal__body">' +
        '<div class="nms-deal__row">' +
          '<img class="nms-deal__img" src="' + img + '" alt="" loading="lazy">' +
          '<div class="nms-deal__info">' +
            '<div class="nms-deal__name">' + escapeHtml(p.title) + '</div>' +
            '<div class="nms-deal__price"><b>' + inr(p.price) + '</b>' +
              (p.compare_at_price > p.price ? '<s>' + inr(p.compare_at_price) + '</s>' : '') +
              (off ? '<em>' + off + '</em>' : '') + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="nms-deal__row">' +
          '<select class="nms-deal__size" aria-label="Size">' + opts + '</select>' +
          '<button type="button" class="nms-deal__add">Add to Cart</button>' +
        '</div>' +
      '</div>'
    );
  }

  function dealInCart(sr, p) {
    var titles = sr.querySelectorAll('.sidecart-product-title');
    for (var i = 0; i < titles.length; i++) {
      if (titles[i].textContent.trim() === p.title) return true;
    }
    return false;
  }

  function ensureDeal(sr) {
    var wrap = sr.querySelector('.side-cart-content-wrapper');
    if (!wrap) return;
    var el = wrap.querySelector('.nms-deal');
    if (!deal.product) { loadDeal(); return; }
    // Once the deal product is in the cart the block goes; it comes back when the item is removed.
    if (dealInCart(sr, deal.product)) { if (el) el.remove(); deal.added = false; return; }
    if (!el) {
      var html = dealHtml(deal.product);
      if (!html) return;
      el = document.createElement('div');
      el.className = 'nms-deal';
      el.innerHTML = html;
      wrap.appendChild(el);
      if (deal.added) markAdded(el);
    }
    var t = timeLeft();
    var parts = el.querySelectorAll('.nms-deal__timer b');
    for (var i = 0; i < parts.length; i++) {
      var v = t[parts[i].getAttribute('data-u')];
      if (parts[i].textContent !== v) parts[i].textContent = v;
    }
  }

  function markAdded(el) {
    var b = el.querySelector('.nms-deal__add');
    if (b) { b.disabled = true; b.textContent = 'Added'; }
  }

  function onRootClick(e) {
    onSizeClick(e);
    var btn = e.target.closest && e.target.closest('.nms-deal__add');
    if (!btn || btn.disabled) return;
    var el = btn.closest('.nms-deal');
    var sel = el.querySelector('.nms-deal__size');
    btn.disabled = true;
    btn.textContent = 'Adding';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(sel.value, 10), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      deal.added = true;
      markAdded(el);
      if (typeof window.refreshSideCart === 'function') window.refreshSideCart();
    }).catch(function () {
      // Usually a size that sold out since the product was fetched: say so, then
      // drop the cached product so the next tick re-renders with fresh availability.
      btn.textContent = 'Sold out';
      setTimeout(function () { deal.product = null; el.remove(); }, 1500);
    });
  }

  /* ---------- quick view sheet ---------- */

  function money(t) { return parseFloat(String(t || '').replace(/[^\d.]/g, '')) || 0; }

  function syncSheet(sr) {
    var sh = sr.querySelector('.sheet-wrapper .product-popup-content');
    if (!sh) return;
    // "Name : Colour - UK 8" -> name line + colour line (size lives in the pills).
    var h = sh.querySelector('.product-title');
    if (h && !h.querySelector('.nms-pt-name')) {
      var parts = h.textContent.trim().replace(/\s+-\s+UK\s*[\d.]+$/i, '').split(/\s*:\s*/);
      h.innerHTML = '<span class="nms-pt-name">' + escapeHtml(parts[0]) + '</span>' +
        (parts.length > 1 ? '<span class="nms-pt-colour">' + escapeHtml(parts.slice(1).join(' : ')) + '</span>' : '');
    }
    var pr = sh.querySelector('.product-price');
    if (pr && !pr.querySelector('.nms-save')) {
      var was = money((pr.querySelector('strike') || {}).textContent);
      var now = money((pr.querySelector('.price') || {}).textContent);
      if (was > now) {
        var save = document.createElement('span');
        save.className = 'nms-save';
        save.textContent = 'Save ₹' + (was - now).toLocaleString('en-IN', { maximumFractionDigits: 2 });
        pr.appendChild(save);
      }
    }
    var dots = sh.querySelectorAll('.carousel-dots .dot');
    var mic = sh.querySelector('.main-image-container');
    if (mic && dots.length > 1) {
      var idx = 0;
      for (var i = 0; i < dots.length; i++) if (dots[i].classList.contains('active')) idx = i;
      var count = (idx + 1) + '/' + dots.length;
      if (mic.getAttribute('data-nms-count') !== count) mic.setAttribute('data-nms-count', count);
    }
    var sel = sh.querySelector('.variant-selector select');
    if (!sel || !sel.options.length || (sel.options.length === 1 && /default title/i.test(sel.options[0].text))) return;
    var list = sel.closest('.variant-list') || sel.parentNode;
    var box = list.querySelector('.nms-sizes');
    var key = [].map.call(sel.options, function (o) { return o.text + (o.disabled ? '!' : ''); }).join('|');
    if (!box || box.getAttribute('data-key') !== key) {
      if (!box) { box = document.createElement('div'); box.className = 'nms-sizes'; list.appendChild(box); }
      box.setAttribute('data-key', key);
      box.innerHTML = [].map.call(sel.options, function (o) {
        return '<button type="button" class="nms-size" data-v="' + escapeHtml(o.value) + '"' +
          (o.disabled ? ' disabled' : '') + '>' + escapeHtml(o.text) + '</button>';
      }).join('');
      // Upsell sheet only (it has the Add button): start on the most-bought size instead of GoKwik's
      // first option. Never in the line-item sheet, where a select change swaps the cart variant.
      if (sh.querySelector('.add-to-cart-section') && sel.selectedIndex === 0 && !box.hasAttribute('data-def')) {
        box.setAttribute('data-def', '1');
        for (var k = 0; k < sel.options.length; k++) {
          if (!sel.options[k].disabled && sel.options[k].text.trim() === CFG.sheetDefaultSize) {
            sel.value = sel.options[k].value;
            sel.dispatchEvent(new Event('input', { bubbles: true }));
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }
    var pills = box.querySelectorAll('.nms-size');
    for (var j = 0; j < pills.length; j++) {
      var on = pills[j].getAttribute('data-v') === sel.value;
      if (pills[j].classList.contains('is-on') !== on) pills[j].classList.toggle('is-on', on);
    }
    // "Add to cart · UK 8". Only touches the idle label, so GoKwik's "Adding…" states show as-is.
    var span = sh.querySelector('.add-to-cart-btn span');
    if (span && /^\+?\s*add to cart(\s*·.*)?$/i.test(span.textContent.trim())) {
      var want = 'Add to cart · ' + ((sel.options[sel.selectedIndex] || {}).text || '');
      if (span.textContent !== want) span.textContent = want;
    }
  }

  function onSizeClick(e) {
    var b = e.target.closest && e.target.closest('.nms-size');
    if (!b || b.disabled) return;
    var sel = b.closest('.variant-list').querySelector('select');
    if (!sel || sel.value === b.getAttribute('data-v')) return;
    sel.value = b.getAttribute('data-v');
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------- upsell title + tier ticks ---------- */

  function syncUpsellTitle(sr, n) {
    var t = sr.querySelector('.upSell-container .title');
    var want = CFG.upsellTitle[Math.min(Math.max(n, 1), 4)];
    if (t && want && t.textContent.trim() !== want) t.textContent = want;
  }

  function syncTierTicks(sr, n) {
    var icons = sr.querySelectorAll('.progress-container .icon-wrap');
    for (var i = 0; i < icons.length; i++) {
      var on = n >= (CFG.tiers[i] || Infinity);
      if (icons[i].classList.contains('nms-on') !== on) icons[i].classList.toggle('nms-on', on);
    }
  }

  /* ---------- floating cart button ---------- */

  /* Two looks, one element. BAR (no sticky bottom bar on the page): dark card with the last-added
     thumbnail, "2 items · ₹1,672" and the tier hint; collapses to MINI on scroll down, returns on scroll up.
     MINI (page has a sticky bottom bar such as the PDP buy buttons or the collection Filter/Sort bar,
     or the BAR was collapsed): round icon + count badge, sitting above that bar. */
  var FAB_CSS = [
    '.nms-fab{position:fixed;right:16px;bottom:calc(var(--nms-fab-b,20px) + env(safe-area-inset-bottom,0px));z-index:1000;display:none;align-items:center;height:52px;min-width:52px;max-width:calc(100vw - 92px);padding:0 14px;border:0;border-radius:999px;background:#1C1C1C;color:#fff;font-family:inherit;font-size:14px;font-weight:600;line-height:1;text-align:left;box-shadow:0 6px 18px rgba(0,0,0,.28);cursor:pointer;-webkit-tap-highlight-color:transparent;transition:bottom .25s ease}',
    '.nms-fab.is-on{display:inline-flex;animation:nms-fab-in .3s ease both}',
    '.nms-fab__mini{display:inline-flex;align-items:center}',
    '.nms-fab__mini svg{width:24px;height:24px;flex:none}',
    '.nms-fab__t{display:inline-block;max-width:0;overflow:hidden;white-space:nowrap;opacity:0;transition:max-width .35s ease,opacity .25s ease,margin .35s ease}',
    '.nms-fab.is-open .nms-fab__t{max-width:190px;opacity:1;margin-left:10px}',
    '.nms-fab__n{position:absolute;top:-4px;right:-4px;min-width:22px;height:22px;padding:0 5px;box-sizing:border-box;border:2px solid #fff;border-radius:999px;background:#175615;color:#fff;font-size:11px;font-weight:700;line-height:18px;text-align:center}',
    '.nms-fab__bar{display:none;align-items:center;gap:10px;min-width:0}',
    '.nms-fab__img{width:44px;height:44px;flex:none;border-radius:8px;object-fit:cover;background:#F3F2EE}',
    '.nms-fab__txt{display:flex;flex-direction:column;gap:4px;min-width:0}',
    '.nms-fab__l1{font-size:14px;font-weight:700;line-height:18px;white-space:nowrap}',
    '.nms-fab__l2{font-size:11px;font-weight:600;line-height:14px;color:#9BD39A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '.nms-fab__go{width:18px;height:18px;flex:none}',
    '.nms-fab.is-bar{left:0;right:0;width:100%;max-width:none;height:calc(64px + env(safe-area-inset-bottom,0px));padding:0 16px env(safe-area-inset-bottom,0px) 12px;border-radius:16px 16px 0 0;box-shadow:0 -4px 16px rgba(0,0,0,.18)}',
    '.nms-fab.is-bar .nms-fab__bar{flex:1;width:100%}',
    '.nms-fab.is-bar .nms-fab__txt{flex:1}',
    '.nms-fab.is-bar .nms-fab__go{width:32px;height:32px;padding:7px;box-sizing:border-box;border-radius:50%;background:#fff;stroke:#1C1C1C}',
    'body.nms-fab-bar{padding-bottom:calc(64px + env(safe-area-inset-bottom,0px))}',
    'body.nms-fab-bar .nms-search-flyout,body.nms-fab-bar .nms-whatsuphelp{translate:0 -72px;transition:translate .25s ease}',
    // MINI sits where the theme's floating cart button sat: middle of the right edge (12px from it on desktop).
    '.nms-fab:not(.is-bar){top:calc(50% - 26px);bottom:auto}',
    '@media (min-width:768px){.nms-fab:not(.is-bar){right:12px}}',
    '.nms-fab.is-bar .nms-fab__mini,.nms-fab.is-bar .nms-fab__n{display:none}',
    '.nms-fab.is-bar .nms-fab__bar{display:flex}',
    '.nms-fab.is-bump{animation:nms-fab-bump .6s ease}',
    '@keyframes nms-fab-in{from{opacity:0;transform:translateY(14px) scale(.94)}to{opacity:1;transform:none}}',
    '@keyframes nms-fab-bump{0%{transform:scale(1)}30%{transform:scale(1.12)}60%{transform:scale(.97)}100%{transform:scale(1)}}',
    '@media (prefers-reduced-motion:reduce){.nms-fab,.nms-fab.is-on,.nms-fab.is-bump{animation:none}.nms-fab__t{transition:none}.nms-fab{transition:none}}'
  ].join('\n');

  var fab = { el: null, count: 0, total: 0, thumb: '', drawer: false, typing: false, collapsed: false,
              stickyH: -1, tOpen: 0, n: 0, y: 0, raf: 0, mode: '', viewed: {} };

  function fabPage() {
    return !/^\/(cart|checkouts?|account|orders)(\/|$)/.test(location.pathname);
  }

  function fabTrack(evt) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: evt, fab_state: fab.mode, fab_items: fab.count, fab_value: Math.round(fab.total / 100), page_path: location.pathname });
    } catch (e) { /* tracking must never break the cart */ }
  }

  function fabEnsure() {
    if (fab.el) return fab.el;
    var st = document.createElement('style');
    st.id = 'nms-fab-css';
    st.textContent = FAB_CSS + (CFG.fab.slide ? '' : '.nms-fab.is-on{animation:none}');
    document.head.appendChild(st);
    var b = document.createElement('button');
    b.type = 'button';
    b.id = 'nms-floating-cart-kwik';
    b.className = 'nms-fab';
    b.setAttribute('aria-label', 'View cart');
    b.innerHTML =
      '<span class="nms-fab__mini"><svg viewBox="0 0 25 24" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8.5874 19.5005C9.07065 19.5005 9.4624 19.8922 9.4624 20.3755C9.4624 20.8587 9.07065 21.2505 8.5874 21.2505C8.10415 21.2505 7.7124 20.8587 7.7124 20.3755C7.7124 19.8922 8.10415 19.5005 8.5874 19.5005Z"/><path d="M18.3374 19.5005C18.8207 19.5005 19.2124 19.8922 19.2124 20.3755C19.2124 20.8587 18.8207 21.2505 18.3374 21.2505C17.8542 21.2505 17.4624 20.8587 17.4624 20.3755C17.4624 19.8922 17.8542 19.5005 18.3374 19.5005Z"/><path d="M5.05209 6.87549H13.5697M1.8374 3.12549H3.41428C3.5772 3.12552 3.73568 3.1786 3.86575 3.27671C3.99582 3.37481 4.09041 3.5126 4.13521 3.66924L7.52615 15.538C7.6158 15.8514 7.80512 16.1271 8.06546 16.3233C8.32579 16.5196 8.64296 16.6256 8.96896 16.6255H17.9793C18.3001 16.6257 18.6126 16.523 18.8707 16.3325C19.1289 16.142 19.3192 15.8737 19.4137 15.5671L20.0575 13.474"/></svg><span class="nms-fab__t"></span></span>' +
      '<span class="nms-fab__bar"><img class="nms-fab__img" alt="" width="44" height="44"><span class="nms-fab__txt"><span class="nms-fab__l1"></span><span class="nms-fab__l2"></span></span>' +
      '<svg class="nms-fab__go" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></span>' +
      '<span class="nms-fab__n" aria-hidden="true"></span>';
    b.addEventListener('click', function () {
      fabTrack('cart_fab_click');
      if (typeof window.openGokwikSideCart === 'function') window.openGokwikSideCart();
      else location.href = '/cart'; // GoKwik's opener missing (e.g. after a release): fall back to the cart page
    });
    document.body.appendChild(b);
    fab.el = b;
    fab.y = window.pageYOffset || 0;
    // Scroll down collapses the BAR to the icon; scroll up (or the top of the page) brings it back.
    window.addEventListener('scroll', function () {
      if (fab.raf) return;
      fab.raf = window.requestAnimationFrame(function () {
        fab.raf = 0;
        var y = window.pageYOffset || 0, d = y - fab.y;
        if (Math.abs(d) < 12) return;
        fab.y = y;
        var want = d > 0 && y > 120;
        if (d < 0 || y <= 120) want = false;
        if (want !== fab.collapsed) { fab.collapsed = want; fabRender(false); }
      });
    }, { passive: true });
    // Hide while a text field is focused (on-screen keyboard, search).
    document.addEventListener('focusin', function (e) {
      var t = e.target && e.target.tagName;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(t || '')) { fab.typing = true; fabRender(false); }
    });
    document.addEventListener('focusout', function () {
      setTimeout(function () {
        var t = document.activeElement && document.activeElement.tagName;
        fab.typing = /^(INPUT|TEXTAREA|SELECT)$/.test(t || '');
        fabRender(false);
      }, 120);
    });
    return b;
  }

  function fabTier(n) {
    var t = CFG.fab.tier;
    return t[Math.min(Math.max(n, 1), 3)] || '';
  }

  function fabRender(added, text) {
    var b = fabEnsure();
    var show = CFG.features.floatCart && fab.count > 0 && !fab.drawer && !fab.typing && fabPage();
    if (b.classList.contains('is-on') !== show) b.classList.toggle('is-on', show);
    if (!show) { b.classList.remove('is-open'); document.body.classList.remove('nms-fab-bar'); fab.mode = ''; return; }
    if (added) fab.collapsed = false;
    if (fab.stickyH < 0) fab.stickyH = fabStickyH(); // measure before the first paint so the PDP never flashes the BAR
    var bar = CFG.fab.bar && fab.stickyH === 0 && !fab.collapsed;
    var mode = bar ? 'bar' : 'mini';
    if (b.classList.contains('is-bar') !== bar) b.classList.toggle('is-bar', bar);
    if (document.body.classList.contains('nms-fab-bar') !== bar) document.body.classList.toggle('nms-fab-bar', bar);
    var px = bar ? CFG.fab.barBottom : CFG.fab.miniBottom + fab.stickyH;
    if (b.style.getPropertyValue('--nms-fab-b') !== px + 'px') b.style.setProperty('--nms-fab-b', px + 'px');
    var word = fab.count === 1 ? ' item' : ' items';
    var l1 = fab.count + word + ' · ' + inr(fab.total);
    var q = function (s) { return b.querySelector(s); };
    var set = function (el, v) { if (el.textContent !== v) el.textContent = v; };
    set(q('.nms-fab__n'), fab.count > 99 ? '99+' : String(fab.count));
    set(q('.nms-fab__l1'), l1);
    set(q('.nms-fab__l2'), fabTier(fab.count));
    var img = q('.nms-fab__img');
    if (fab.thumb && img.getAttribute('src') !== fab.thumb) img.setAttribute('src', fab.thumb);
    img.style.visibility = fab.thumb ? 'visible' : 'hidden';
    b.setAttribute('aria-label', 'View cart, ' + l1);
    if (mode !== fab.mode) {
      fab.mode = mode;
      if (!fab.viewed[mode]) { fab.viewed[mode] = 1; fabTrack('cart_fab_view'); }
    }
    if (added) {
      set(q('.nms-fab__t'), text || 'Added · View cart');
      b.classList.remove('is-bump');
      void b.offsetWidth; // restart the animation
      b.classList.add('is-bump');
      if (!bar && CFG.fab.slide) { b.classList.add('is-open'); fab.tOpen = Date.now(); }
    }
  }

  function fabRefresh(added, mode) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c) return;
        fab.count = c.item_count || 0;
        fab.total = c.total_price || 0;
        // Newest line first: its image is the "last added" thumbnail.
        var it = c.items && c.items[0];
        var u = it && (it.image || (it.featured_image && it.featured_image.url)) || '';
        fab.thumb = u ? u.replace(/(\.[a-z]+)(\?|$)/i, '_88x88$1$2') : '';
        fabRender(!!added && fab.count > 0, mode === 'close' ? 'View cart · ' + inr(fab.total) : '');
      })
      .catch(function () {});
  }

  // Height of a real sticky bottom bar (PDP buy buttons, collection Filter/Sort, any fixed bar anchored
  // to the bottom). Transparent wrappers and third-party frames (e.g. #PBarNextFrameWrapper) are ignored.
  function fabStickyH() {
    var vh = window.innerHeight, vw = window.innerWidth, best = 0;
    var seen = [];
    var test = function (e) {
      if (!e || e === fab.el || seen.indexOf(e) > -1) return;
      seen.push(e);
      var cs = getComputedStyle(e);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return;
      var r = e.getBoundingClientRect();
      if (r.height < 20 || r.height > vh * 0.4 || r.width < vw * 0.6 || vh - r.bottom > 2 || r.bottom <= 0) return;
      var kids = e.children;
      if (kids.length && [].every.call(kids, function (k) { return k.tagName === 'IFRAME'; })) return;
      var bg = cs.backgroundColor;
      var visible = !(bg === 'transparent' || /rgba\(.*,\s*0\)$/.test(bg)) || (e.textContent || '').trim().length > 0;
      if (visible && r.height > best) best = Math.round(r.height);
    };
    var sel = CFG.fab.sticky;
    for (var i = 0; i < sel.length; i++) {
      var list = document.querySelectorAll(sel[i]);
      for (var j = 0; j < list.length; j++) test(list[j]);
    }
    var kids = document.body.children;
    for (var k = 0; k < kids.length; k++) {
      if (kids[k].tagName === 'A' || kids[k].tagName === 'SCRIPT' || kids[k].tagName === 'STYLE') continue;
      test(kids[k]);
    }
    return best;
  }

  function fabSync(sr, n) {
    if (!CFG.features.floatCart || !document.body) return;
    var open = !!(sr && sr.querySelector('.sidecart'));
    if (open !== fab.drawer) {
      fab.drawer = open;
      document.body.classList.toggle('nms-kwikcart-open', open); // style the page while the GoKwik drawer is open
      if (!open) fabRefresh(true, 'close'); // drawer edits change the count; the nudge brings the user back to the cart
    }
    if (open && n !== fab.count && n >= 0) { fab.count = n; }
    if (!fab.el) return;
    if (fab.el.classList.contains('is-open') && Date.now() - fab.tOpen > 3200) fab.el.classList.remove('is-open');
    if (fab.el.classList.contains('is-on') && (fab.n++ % 2 === 0)) {
      var h = fabStickyH();
      if (h !== fab.stickyH) { fab.stickyH = h; fabRender(false); }
    }
    fabRender(false);
  }

  function fabInit() {
    if (!CFG.features.floatCart || window.__nmsFab) return;
    window.__nmsFab = true;
    var _f = window.fetch;
    if (typeof _f === 'function') {
      window.fetch = function (input) {
        var u = typeof input === 'string' ? input : (input && input.url) || '';
        var p = _f.apply(this, arguments);
        if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u)) {
          var added = /\/cart\/add/.test(u);
          p.then(function (r) { if (r && r.ok) fabRefresh(added); }).catch(function () {});
        }
        return p;
      };
    }
    // Back/forward cache restores a stale count; ask again.
    window.addEventListener('pageshow', function (e) { if (e.persisted) fabRefresh(false); });
    var go = function () { fabEnsure(); fabRefresh(false); };
    if (document.body) go(); else document.addEventListener('DOMContentLoaded', go);
  }

  /* ---------- ?view=cart ---------- */

  // Mirrors the drawer in the URL with history.replaceState, so the page never reloads and Back is unchanged.
  // No template is named *.cart.json, so a fresh load of ?view=cart renders the normal page and the drawer opens on top.
  var uv = { open: false };

  function uvSet(on) {
    try {
      var u = new URL(location.href);
      if (on === (u.searchParams.get('view') === CFG.urlView)) return;
      if (on) u.searchParams.set('view', CFG.urlView); else u.searchParams.delete('view');
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) { /* ignore */ }
  }

  function uvSync(sr) {
    var open = !!(sr && sr.querySelector('.sidecart'));
    if (open !== uv.open) { uv.open = open; uvSet(open); }
  }

  function uvInit() {
    if (!CFG.urlView || !/[?&]view=/.test(location.search)) return;
    if (new URLSearchParams(location.search).get('view') !== CFG.urlView) return;
    var tries = 0;
    var t = setInterval(function () {
      if (typeof window.openGokwikSideCart === 'function') { clearInterval(t); window.openGokwikSideCart(); }
      else if (++tries > 40) { clearInterval(t); uvSet(false); } // GoKwik never loaded (~20 s): drop the param
    }, 500);
  }

  /* ---------- fail-safe guard ---------- */

  var guard = { off: false, openAt: 0, bad: 0 };

  function guardKilled() {
    var q = location.search;
    try {
      if (/[?&]nms_cart=on(&|$)/.test(q)) sessionStorage.removeItem('nms-cart-off');
      if (/[?&]nms_cart=off(&|$)/.test(q)) sessionStorage.setItem('nms-cart-off', '1');
      return sessionStorage.getItem('nms-cart-off') === '1';
    } catch (e) {
      return /[?&]nms_cart=off(&|$)/.test(q);
    }
  }

  // Back to GoKwik's stock look: remove our style and every block we added to the drawer.
  function guardTrip(sr, why) {
    guard.off = true;
    try {
      var rm = sr.querySelectorAll('#nms-css,.nms-deal,.nms-sizes');
      for (var i = 0; i < rm.length; i++) rm[i].remove();
      sessionStorage.setItem('nms-cart-off', '1');
    } catch (e) { /* ignore */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'nms_cart_guard_off', reason: why, page_path: location.pathname });
      if (window.console && console.warn) console.warn('[nms-cart] guard off: ' + why);
    } catch (e) { /* ignore */ }
  }

  // Runs only while the drawer has been open for a while with items in it. Needs 3 bad ticks in a row.
  function guardCheck(sr, n) {
    if (!sr.querySelector('.sidecart')) { guard.openAt = 0; guard.bad = 0; return; }
    if (!guard.openAt) guard.openAt = Date.now();
    if (Date.now() - guard.openAt < 2500 || n < 1) return;
    var why = '';
    var btn = sr.querySelector('.gokwik-checkout button');
    if (!btn || !sr.querySelector('.side-cart-content-wrapper') || !sr.querySelector('.sidecart-footer-wrapper')) {
      why = 'anchors_missing';
    } else {
      var r = btn.getBoundingClientRect();
      if (r.height < 30 || r.width < 120 || r.bottom <= 0 || r.top >= window.innerHeight) why = 'checkout_unreachable';
    }
    if (why) { if (++guard.bad >= 3) guardTrip(sr, why); } else guard.bad = 0;
  }

  /* ---------- tick ---------- */

  var bound = false;

  function tick() {
    var sr = root();
    try { fabSync(sr, sr ? itemCount(sr) : -1); } catch (e) { /* floating cart must never stop the skin */ }
    if (CFG.urlView) { try { uvSync(sr); } catch (e) { /* ignore */ } }
    if (!sr || guard.off) return;
    if (!sr.querySelector('#nms-css')) {
      var s = document.createElement('style');
      s.id = 'nms-css';
      s.textContent = CSS;
      sr.appendChild(s);
    }
    // Capture phase: GoKwik stops click propagation inside the drawer.
    if (!bound) { sr.addEventListener('click', onRootClick, true); bound = true; }
    var n = itemCount(sr);
    // Each feature on its own: one failing never takes the others (or the cart) down.
    try { guardCheck(sr, n); } catch (e) { /* ignore */ }
    if (guard.off) return;
    if (CFG.features.deal) { try { ensureDeal(sr); } catch (e) { /* ignore */ } }
    if (CFG.features.upsellTitle) { try { syncUpsellTitle(sr, n); } catch (e) { /* ignore */ } }
    if (CFG.features.tierTicks) { try { syncTierTicks(sr, n); } catch (e) { /* ignore */ } }
    if (CFG.features.sheet) { try { syncSheet(sr); } catch (e) { /* ignore */ } }
  }

  guard.off = guardKilled();
  fabInit();
  uvInit();
  setInterval(function () { try { tick(); } catch (e) { /* never break the cart */ } }, 700);
})();
